import React from 'react';
import { DIVISIONS, DIVISION_COLORS, deriveDivisions, isAdminProvider, isFieldProvider } from '@/lib/providerDivisions';

// Five equal slices, 12 o'clock clockwise: Janitorial, Landscaping, Security, Sweeping, General Maintenance.
// A slice is filled with its brand color when the provider OFFERS that division, otherwise white
// with a visible border. Slice color = services offered (NOT qualification).
function wedgePath(cx, cy, r, i) {
  const a0 = (-90 + i * 72) * (Math.PI / 180);
  const a1 = (-90 + (i + 1) * 72) * (Math.PI / 180);
  const x0 = cx + r * Math.cos(a0);
  const y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1);
  const y1 = cy + r * Math.sin(a1);
  return `M${cx},${cy} L${x0.toFixed(2)},${y0.toFixed(2)} A${r},${r} 0 0 1 ${x1.toFixed(2)},${y1.toFixed(2)} Z`;
}

export const AdminBadge = ({ className = '' }) => (
  <span className={`inline-flex items-center rounded-full bg-[#2F6DB5]/10 px-2 py-0.5 text-[10px] font-bold text-[#2F6DB5] ${className}`}
    title="Administrative & Business Services" data-testid="admin-badge">Admin &amp; Business</span>
);

export default function ServiceSlices({ provider, size = 28, testid }) {
  const { divisions, incomplete } = deriveDivisions(provider);
  const admin = isAdminProvider(provider);
  const field = isFieldProvider(provider);
  const r = size / 2;
  // Administrative-only provider: show the Admin badge instead of a misleading all-white field pie.
  if (admin && !field) {
    return <AdminBadge />;
  }
  const offeredLabel = divisions.length ? divisions.join(', ') : 'Services not recorded — needs completion';
  return (
    <span className="inline-flex items-center gap-1" title={offeredLabel} aria-label={`Services offered: ${offeredLabel}`}
      data-testid={testid || `service-slices-${provider?.id || 'x'}`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
        {DIVISIONS.map((d, i) => {
          const on = divisions.includes(d);
          return (
            <path key={d} d={wedgePath(r, r, r - 0.6, i)}
              fill={on ? DIVISION_COLORS[d] : '#ffffff'}
              stroke={incomplete ? '#f59e0b' : '#cbd5e1'} strokeWidth={0.8}
              data-testid={`slice-${d.replace(/\s/g, '')}-${on ? 'on' : 'off'}`} />
          );
        })}
      </svg>
      {admin && <AdminBadge />}
    </span>
  );
}

export function DivisionLegend() {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1" data-testid="division-legend">
      {DIVISIONS.map((d) => (
        <span key={d} className="inline-flex items-center gap-1 text-[11px] text-slate-600">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: DIVISION_COLORS[d] }} />{d}
        </span>
      ))}
    </div>
  );
}
